import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getCurrentUser, logoutUser } from './services/auth';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';
import Dashboard from './components/Dashboard';
import Auth from './components/Auth';
import Navbar from './components/Navbar';
import CommunicationCenter from './components/communications/CommunicationCenter';
import LandingPage from './pages/public/LandingPage';
import Pricing from './components/Pricing';
import PublicNavbar from './components/PublicNavbar';
import OrdersPage from './pages/OrdersPage';
import OrdersPageEnhanced from './pages/OrdersPageEnhanced';
import MessagingPage from './pages/MessagingPage';
import ShopifyRedirect from './components/ShopifyRedirect';
import ConnectShopify from './components/ConnectShopify';
import './styles/main.css';
import './styles/icons.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shopifyUrl, setShopifyUrl] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Ne pas déconnecter l'utilisateur au démarrage
        // Vérifier simplement s'il y a un utilisateur connecté
        const currentUser = await getCurrentUser();
        console.log('Utilisateur actuel:', currentUser ? currentUser.email : 'Aucun');
        setUser(currentUser);
      } catch (error) {
        console.error('Erreur de vérification auth:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleAuthSuccess = (user) => {
    console.log('Authentification réussie pour:', user.email);
    // Vérifier si l'utilisateur a une URL Shopify à rediriger
    if (user && user.shopifyUrl) {
      setShopifyUrl(user.shopifyUrl);
      setUser(user);
    } else {
      setUser(user);
    }
  };

  const handleShopifyCancel = () => {
    setShopifyUrl(null);
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      setUser(null);
      setShopifyUrl(null);
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <Router>
        <div className="app">
          {user && !shopifyUrl ? (
            <Navbar user={user} onLogout={handleLogout} />
          ) : (
            <PublicNavbar />
          )}
          <Routes>
            {/* Routes publiques */}
            <Route 
              path="/" 
              element={
                user ? (
                  shopifyUrl ? 
                  <ShopifyRedirect shopifyUrl={shopifyUrl} onCancel={handleShopifyCancel} /> : 
                  <Navigate to="/dashboard" />
                ) : (
                  <>
                    <LandingPage />
                  </>
                )
              } 
            />
            <Route 
              path="/login" 
              element={
                user ? (
                  shopifyUrl ? 
                  <ShopifyRedirect shopifyUrl={shopifyUrl} onCancel={handleShopifyCancel} /> : 
                  <Navigate to="/dashboard" />
                ) : (
                  <>
                    <Auth onAuthSuccess={handleAuthSuccess} mode="login" />
                  </>
                )
              }
            />
            <Route 
              path="/register" 
              element={
                user ? (
                  shopifyUrl ? 
                  <ShopifyRedirect shopifyUrl={shopifyUrl} onCancel={handleShopifyCancel} /> : 
                  <Navigate to="/dashboard" />
                ) : (
                  <>
                    <Auth onAuthSuccess={handleAuthSuccess} mode="register" />
                  </>
                )
              }
            />
            <Route 
              path="/shopify-redirect" 
              element={
                user ? (
                  <ShopifyRedirect shopifyUrl={shopifyUrl} onCancel={handleShopifyCancel} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route 
              path="/pricing" 
              element={
                <>
                  <Pricing />
                </>
              }
            />

            {/* Routes authentifiées */}
            {user && !shopifyUrl ? (
              <>
                <Route
                  path="/dashboard"
                  element={
                    <>
                      <main className="bg-gray-50 min-h-screen pt-4">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                          <Dashboard user={user} />
                        </div>
                      </main>
                    </>
                  }
                />
                <Route
                  path="/connect-shopify"
                  element={
                    <>
                      <main className="bg-gray-50 min-h-screen pt-4">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                          <ConnectShopify />
                        </div>
                      </main>
                    </>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <>
                      <main className="bg-gray-50 min-h-screen pt-4">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                          <OrdersPageEnhanced />
                        </div>
                      </main>
                    </>
                  }
                />
                <Route
                  path="/messaging"
                  element={
                    <>
                      <main className="bg-gray-50 min-h-screen pt-4">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                          <MessagingPage />
                        </div>
                      </main>
                    </>
                  }
                />
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </>
            ) : (
              <Route path="*" element={<Navigate to="/" />} />
            )}
          </Routes>
        </div>
      </Router>
    </I18nextProvider>
  );
}

export default App;
